<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="t('Conciliação de Adiantamentos de Colaboradores')"
    :steps="wizardStepsConfig"
    size="xlarge"
    :show-summary="currentStepIndex >= 3"
    :summary-items="summaryItems"
    :reset-on-close="true"
    :loading="isProcessing"
    @close="handleClose"
    @cancel="handleClose"
    @step-change="handleStepChange"
    @finish="handleFinish">

    <!-- Step 0: Current Advances Status -->
    <template #step-0>
      <div class="wizard-step-content" data-testid="payroll-advances-step-status">
        <div class="step-header">
          <h4>{{ t('Status dos Adiantamentos') }}</h4>
          <p class="step-subtitle">
            {{ t('Revise os adiantamentos já marcados para este período de folha') }}
          </p>
        </div>

        <div v-if="isLoadingAdvances" class="loading-container">
          <spinner />
        </div>

        <div v-else>
          <!-- KPI Cards -->
          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-icon info">
                <i class="pi pi-users"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value">{{ uniqueEmployeesWithAdvances }}</span>
                <span class="kpi-label">{{ t('Colaboradores com Adiantamentos') }}</span>
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-icon success">
                <i class="pi pi-wallet"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value">{{ existingAdvancesSelected }}</span>
                <span class="kpi-label">{{ t('Adiantamentos Selecionados') }}</span>
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-icon warning">
                <i class="pi pi-dollar"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value">{{ n(existingAdvancesTotal, 'currency') }}</span>
                <span class="kpi-label">{{ t('Total a Descontar') }}</span>
              </div>
            </div>
          </div>

          <!-- Current Advances by Employee -->
          <div v-if="employeesWithAdvances.length > 0" class="current-advances-section">
            <h5 class="section-title">{{ t('Adiantamentos Atuais por Colaborador') }}</h5>

            <div v-for="employee in employeesWithAdvances" :key="String(employee.employee_id)" class="employee-section">
              <div class="employee-header">
                <div class="employee-info">
                  <i class="pi pi-user"></i>
                  <span class="employee-name">{{ employee.employee_name }}</span>
                </div>
                <div class="employee-total">
                  <span class="total-label">{{ t('Total') }}:</span>
                  <span class="total-value">{{ n(getEmployeeTotal(employee), 'currency') }}</span>
                </div>
              </div>

              <div class="advances-list">
                <div v-for="advance in employee.advances" :key="String(advance.id)" class="advance-item">
                  <div class="advance-info">
                    <status-tag
                      :value="advance.source_type_label"
                      :severity="advance.source_type_severity"
                      :icon="getSourceTypeIcon(advance.source_type)" />
                    <span class="advance-title">{{ advance.title }}</span>
                    <span class="advance-date">{{ formatDate(advance.request_date_string || advance.request_date || (advance as AdvanceRow).created_at) }}</span>
                  </div>
                  <div class="advance-amounts">
                    <div class="amount-row">
                      <span class="amount-label">{{ t('Total') }}:</span>
                      <span class="amount-value">{{ n(advance.amount, 'currency') }}</span>
                    </div>
                    <div class="amount-row">
                      <span class="amount-label">{{ t('Restante') }}:</span>
                      <span class="amount-value amount-remaining">{{ n(advance.amount_remaining, 'currency') }}</span>
                    </div>
                    <div v-if="selectedDiscounts[String(advance.id)]" class="amount-row selected">
                      <span class="amount-label">{{ t('Selecionado') }}:</span>
                      <span class="amount-value amount-selected">{{ n(parseFloat(String(selectedDiscounts[String(advance.id)] ?? 0)) || 0, 'currency') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <i class="pi pi-check-circle"></i>
            <h4>{{ t('Nenhum adiantamento selecionado') }}</h4>
            <p>{{ t('Avance para o próximo passo para selecionar recebimentos em dinheiro como adiantamentos ou selecionar adiantamentos existentes.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 1: Cash Recebimentos (Convert to Advances) -->
    <template #step-1>
      <div class="wizard-step-content" data-testid="payroll-advances-step-cash">
        <div class="step-header">
          <h4>{{ t('Converter Recebimentos em Dinheiro') }}</h4>
          <p class="step-subtitle">
            {{ t('Selecione os recebimentos em dinheiro que foram mantidos pelos operadores') }}
          </p>
        </div>

        <inline-message severity="info" :closable="false" class="info-message">
          <i class="pi pi-info-circle"></i>
          {{ t('Recebimentos em dinheiro marcados como adiantamento serão descontados do valor a pagar ao colaborador.') }}
        </inline-message>

        <div v-if="isLoadingCashRecebimentos" class="loading-container">
          <spinner />
        </div>

        <div v-else>
          <!-- Filters -->
          <div class="filters-row">
            <select-list
              elem-id="payroll_selectedOperadorFilter"
              v-model="selectedOperadorFilter"
              :itens-select="operadorOptions"
              :placeholder="t('Todos Operadores')"
              data-testid="payroll-advances-operator-filter"
              class="filter-dropdown" />
          </div>

          <!-- Available Cash Recebimentos Table -->
          <div v-if="filteredCashRecebimentos.length > 0">
            <data-table
              v-model:selection="selectedCashRecebimentos"
              :config="cashRecebimentosTableConfig"
              :data="filteredCashRecebimentos"
              data-testid="payroll-advances-cash-table"
              class="cash-recebimentos-table" />

            <!-- Selection Summary -->
            <div class="selection-summary">
              <div class="selection-info">
                <span v-if="selectedCashRecebimentos.length > 0">
                  {{ t('Selecionados') }}: <strong>{{ selectedCashRecebimentos.length }}</strong>
                  ({{ n(cashSelectedTotal, 'currency') }})
                </span>
                <span v-else class="no-selection">
                  {{ t('Selecione recebimentos para converter em adiantamentos') }}
                </span>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <i class="pi pi-inbox"></i>
            <h4>{{ t('Nenhum recebimento em dinheiro disponível') }}</h4>
            <p>{{ t('Não há recebimentos em dinheiro no período que possam ser marcados como adiantamento.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 2: Select Advances to Discount -->
    <template #step-2>
      <div class="wizard-step-content" data-testid="payroll-advances-step-select">
        <div class="step-header">
          <h4>{{ t('Selecionar Adiantamentos para Desconto') }}</h4>
          <p class="step-subtitle">
            {{ t('Selecione os adiantamentos e os valores a descontar neste período') }}
          </p>
        </div>

        <div v-if="isLoadingAdvances" class="loading-container">
          <spinner />
        </div>

        <div v-else>
          <inline-message severity="warn" :closable="false" class="info-message">
            <i class="pi pi-exclamation-triangle"></i>
            {{ t('O status dos adiantamentos será atualizado automaticamente (aberto → parcial → pago) conforme os descontos são aplicados.') }}
          </inline-message>

          <!-- EmployeeDiscountCard components for each employee -->
          <div v-if="employeesWithAdvances.length > 0" class="employees-list">
            <EmployeeDiscountCard
              v-for="employee in employeesWithAdvances"
              :key="String(employee.employee_id)"
              :employee="(employee as unknown) as EmployeeDiscountCardEmployee"
              :items="(employee.advances as unknown) as EmployeeDiscountCardItem[]"
              :selected-discounts="(selectedDiscounts as unknown) as Record<string | number, number>"
              @update:discount="handleDiscountUpdate" />
          </div>

          <div v-else class="empty-state">
            <i class="pi pi-check-circle"></i>
            <h4>{{ t('Nenhum adiantamento disponível') }}</h4>
            <p>{{ t('Não há adiantamentos abertos ou parciais para os colaboradores deste período.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 3: Confirm Selection -->
    <template #step-3>
      <div class="wizard-step-content" data-testid="payroll-advances-step-confirm">
        <div class="step-header">
          <h4>{{ t('Confirmar Seleção') }}</h4>
          <p class="step-subtitle">
            {{ t('Revise e confirme os adiantamentos selecionados para desconto') }}
          </p>
        </div>

        <!-- Final Summary -->
        <div class="final-summary">
          <div class="summary-card">
            <div class="summary-header">
              <i class="pi pi-check-circle"></i>
              <h5>{{ t('Resumo da Seleção') }}</h5>
            </div>
            <div class="summary-body">
              <div class="summary-row">
                <span class="label">{{ t('Recebimentos em dinheiro a converter') }}:</span>
                <span class="value highlight">{{ selectedCashRecebimentos.length }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Valor dos recebimentos') }}:</span>
                <span class="value highlight">{{ n(cashSelectedTotal, 'currency') }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Adiantamentos existentes selecionados') }}:</span>
                <span class="value">{{ existingAdvancesSelected }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Valor dos adiantamentos existentes') }}:</span>
                <span class="value">{{ n(existingAdvancesTotal, 'currency') }}</span>
              </div>
              <div class="summary-row total">
                <span class="label">{{ t('Total a descontar neste período') }}:</span>
                <span class="value">{{ n(grandTotal, 'currency') }}</span>
              </div>
            </div>
          </div>

          <!-- Selected Items Preview -->
          <div v-if="selectedCashRecebimentos.length > 0" class="selected-preview">
            <h5 class="section-title">{{ t('Recebimentos em Dinheiro a Converter') }}</h5>
            <data-table
              :config="selectedCashPreviewConfig"
              :data="selectedCashRecebimentos"
              data-testid="payroll-advances-cash-preview-table"
              class="preview-table compact" />
          </div>

          <div v-if="existingAdvancesSelected > 0" class="selected-preview">
            <h5 class="section-title">{{ t('Adiantamentos Existentes Selecionados') }}</h5>
            <data-table
              :config="selectedAdvancesPreviewConfig"
              :data="selectedAdvancesList"
              data-testid="payroll-advances-existing-preview-table"
              class="preview-table compact" />
          </div>

          <inline-message v-if="selectedCashRecebimentos.length === 0 && existingAdvancesSelected === 0" severity="warn" :closable="false">
            {{ t('Nenhum adiantamento selecionado. Clique em "Voltar" para selecionar recebimentos ou adiantamentos, ou "Finalizar" para continuar sem descontos.') }}
          </inline-message>
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import { useEmployeeAdvances } from '@/composables/useEmployeeAdvances';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import EmployeeDiscountCard from './shared/EmployeeDiscountCard.vue';

// =============================================================================
// TYPES
// =============================================================================

type Id = number | string;
type DiscountAmount = number | string;

interface AdvanceRow {
  id: Id;
  employee_id: Id;
  employee_name: string;
  title: string;
  source_type: string;
  source_type_label: string;
  source_type_severity: string;
  amount: number;
  amount_remaining: number;
  request_date?: string | Date | null;
  request_date_string?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

interface CashRecebimentoRow {
  recebimento_id: Id;
  recebimento_data: string;
  operador_user_id?: string;
  operador_name?: string;
  cliente_nome?: string;
  amount_reais: number | string;
  [key: string]: unknown;
}

interface AdvancesApiResponse {
  advances: AdvanceRow[];
  summary_by_employee: unknown[];
  total_open: number;
  available_cash_recebimentos: CashRecebimentoRow[];
}

interface EmployeeGroup {
  employee_id: Id;
  employee_name: string;
  advances: AdvanceRow[];
}

interface UserMapEntry {
  name?: string;
  user_name?: string;
  [key: string]: unknown;
}

interface OperadorOption {
  ID: string;
  Name: string;
}

interface SummaryItem {
  icon: string;
  label: string;
  value: string;
}

interface WizardStepConfig {
  title: string;
  description: string;
  icon: string;
  validate: () => boolean;
}

interface DiscountUpdatePayload {
  itemId: Id;
  amount: DiscountAmount | null;
}

interface CashAdvancesBatchResult {
  created_count?: number;
  [key: string]: unknown;
}

interface DataTableCheckboxConfig {
  enabled: boolean;
  position?: 'first' | 'last';
  width?: string;
}

interface DataTableColumnConfig {
  data: string;
  title: string;
  sortable?: boolean;
  renderType?: string;
  dataType?: string;
}

interface DataTableConfig {
  tableId: string;
  pagination: boolean;
  pageLength?: number;
  checkbox?: DataTableCheckboxConfig;
  columns: DataTableColumnConfig[];
}

interface SelectedAdvancePreviewRow extends AdvanceRow {
  amount_discounted: DiscountAmount;
}

// Structural shapes expected by EmployeeDiscountCard. Defined locally so we
// don't pull the card's internal types — the runtime values are compatible,
// only the strict TS shape narrows differ (DiscountAmount → number, etc.).
interface EmployeeDiscountCardEmployee {
  employee_id: Id;
  employee_name: string;
  [key: string]: unknown;
}

interface EmployeeDiscountCardItem {
  id: Id;
  title: string;
  amount: number;
  amount_remaining: number;
  source_type?: string;
  request_date?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface CashRecebimentoBatchPayload {
  cashCreated: number;
  advancesProcessed: number;
  selections: Record<string, DiscountAmount>;
  discounts: Array<{ advance_id: string; amount: number }>;
}

// =============================================================================
// PROPS / EMITS
// =============================================================================

const props = withDefaults(
  defineProps<{
    visible?: boolean;
    periodId: number;
    franchiseId: number;
    usersMap?: Record<string, UserMapEntry>;
    initialSelections?: Record<string, DiscountAmount>;
  }>(),
  {
    visible: false,
    usersMap: () => ({}),
    initialSelections: () => ({})
  }
);

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'completed': [payload: CashRecebimentoBatchPayload];
  'closed': [];
  'update:selections': [selections: Record<string, DiscountAmount>];
}>();

const { t, n } = useI18n();
const {
  getOpenAdvancesForPeriod,
  fetchAvailableCashRecebimentos,
  createCashAdvancesBatch,
  processDiscounts,
  ADVANCE_SOURCE_TYPE
} = useEmployeeAdvances();

const { formatDate: formatDateLocale } = useDateFormatter();

// Refs
const isVisible = ref<boolean>(false);
const currentStepIndex = ref<number>(0);
const isProcessing = ref<boolean>(false);

// Data
const advancesData = ref<AdvancesApiResponse>({
  advances: [],
  summary_by_employee: [],
  total_open: 0,
  available_cash_recebimentos: []
});
const selectedDiscounts = ref<Record<string, DiscountAmount>>({}); // { advance_id: amount }
const selectedCashRecebimentos = ref<CashRecebimentoRow[]>([]);
const selectedOperadorFilter = ref<string | null>(null);
const cashRecebimentos = ref<CashRecebimentoRow[]>([]);

// Loading states
const isLoadingAdvances = ref<boolean>(false);
const isLoadingCashRecebimentos = ref<boolean>(false);

// Wizard steps configuration (4 steps)
const wizardStepsConfig = computed<WizardStepConfig[]>(() => [
  {
    title: t('Status'),
    description: t('Adiantamentos atuais'),
    icon: 'pi pi-chart-bar',
    validate: () => true
  },
  {
    title: t('Dinheiro'),
    description: t('Recebimentos disponíveis'),
    icon: 'pi pi-money-bill',
    validate: () => true
  },
  {
    title: t('Selecionar'),
    description: t('Adiantamentos para desconto'),
    icon: 'pi pi-list',
    validate: () => true
  },
  {
    title: t('Confirmar'),
    description: t('Finalizar seleção'),
    icon: 'pi pi-check',
    validate: () => true
  }
]);

// Computed: Group advances by employee
const employeesWithAdvances = computed<EmployeeGroup[]>(() => {
  const byEmployee: Record<string, EmployeeGroup> = {};
  advancesData.value.advances.forEach((a) => {
    const key = String(a.employee_id);
    if (!byEmployee[key]) {
      byEmployee[key] = {
        employee_id: a.employee_id,
        employee_name: a.employee_name,
        advances: []
      };
    }
    byEmployee[key].advances.push(a);
  });
  return Object.values(byEmployee);
});

// Computed: Current advances
const currentAdvances = computed<AdvanceRow[]>(() => advancesData.value.advances);

// Computed: Unique employees with advances
const uniqueEmployeesWithAdvances = computed<number>(() => {
  return employeesWithAdvances.value.length;
});

// Computed: Existing advances selected count
const existingAdvancesSelected = computed<number>(() => {
  return Object.values(selectedDiscounts.value).filter((amount) => Number(amount) > 0).length;
});

// Computed: Existing advances total
const existingAdvancesTotal = computed<number>(() => {
  return Object.values(selectedDiscounts.value).reduce<number>(
    (sum, amount) => sum + (parseFloat(String(amount ?? 0)) || 0),
    0
  );
});

// Computed: Cash recebimentos
const filteredCashRecebimentos = computed<CashRecebimentoRow[]>(() => {
  if (!selectedOperadorFilter.value) {
    return cashRecebimentos.value;
  }
  return cashRecebimentos.value.filter(
    (rec) => rec.operador_user_id === selectedOperadorFilter.value
  );
});

// Computed: Cash selected total
const cashSelectedTotal = computed<number>(() => {
  return selectedCashRecebimentos.value.reduce<number>(
    (sum, rec) => sum + (parseFloat(String(rec.amount_reais ?? 0)) || 0),
    0
  );
});

// Computed: Grand total
const grandTotal = computed<number>(() => {
  return existingAdvancesTotal.value + cashSelectedTotal.value;
});

// Computed: Selected advances list (preview rows for confirm step)
const selectedAdvancesList = computed<SelectedAdvancePreviewRow[]>(() => {
  return currentAdvances.value
    .filter((advance) => Number(selectedDiscounts.value[String(advance.id)] ?? 0) > 0)
    .map((advance) => ({
      ...advance,
      amount_discounted: selectedDiscounts.value[String(advance.id)]
    }));
});

// Computed: Operadores list for the filter dropdown (raw map → SelectList shape)
const operadorOptions = computed<OperadorOption[]>(() => {
  const operadores = new Map<string, OperadorOption>();
  cashRecebimentos.value.forEach((rec) => {
    if (rec.operador_user_id && !operadores.has(rec.operador_user_id)) {
      operadores.set(rec.operador_user_id, {
        ID: rec.operador_user_id,
        Name: getOperadorName(rec.operador_user_id)
      });
    }
  });
  return Array.from(operadores.values());
});

// Helper: Get operador name
const getOperadorName = (userId: string): string => {
  return props.usersMap[userId]?.name ||
         props.usersMap[userId]?.user_name ||
         userId;
};

// Helper: Get employee total
const getEmployeeTotal = (employee: EmployeeGroup): number => {
  return employee.advances
    .filter((a) => Number(selectedDiscounts.value[String(a.id)] ?? 0) > 0)
    .reduce<number>(
      (sum, a) => sum + (parseFloat(String(selectedDiscounts.value[String(a.id)] ?? 0)) || 0),
      0
    );
};

// Helper: Get source type icon
const getSourceTypeIcon = (sourceType: string): string => {
  switch (sourceType) {
    case ADVANCE_SOURCE_TYPE.CASH_RECEBIMENTO:
      return 'pi pi-money-bill';
    case ADVANCE_SOURCE_TYPE.MANUAL:
    default:
      return 'pi pi-pencil';
  }
};

// Helper: Format date — accepts Date | string | null
const formatDate = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return '';
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  return formatDateLocale(date);
};

// Summary items for wizard header (declared after the computeds it depends on)
const summaryItems = computed<SummaryItem[]>(() => [
  {
    icon: 'pi pi-calendar',
    label: t('Período'),
    value: `#${props.periodId}`
  },
  {
    icon: 'pi pi-wallet',
    label: t('Adiantamentos'),
    value: `${existingAdvancesSelected.value} (${n(existingAdvancesTotal.value, 'currency')})`
  },
  {
    icon: 'pi pi-money-bill',
    label: t('Recebimentos'),
    value: `${selectedCashRecebimentos.value.length} (${n(cashSelectedTotal.value, 'currency')})`
  }
]);

// =============================================================================
// DATA TABLE CONFIGS
// =============================================================================

const cashRecebimentosTableConfig = computed<DataTableConfig>(() => ({
  tableId: 'payroll-advances-cash-recebimentos',
  pagination: filteredCashRecebimentos.value.length > 10,
  pageLength: 10,
  checkbox: { enabled: true, position: 'first', width: '3rem' },
  columns: [
    { data: 'recebimento_data', title: t('Data'), renderType: 'date', dataType: 'date' },
    { data: 'operador_name', title: t('Operador') },
    { data: 'cliente_nome', title: t('Cliente') },
    { data: 'amount_reais', title: t('Valor'), renderType: 'currency', dataType: 'number' }
  ]
}));

const selectedCashPreviewConfig = computed<DataTableConfig>(() => ({
  tableId: 'payroll-advances-selected-cash-preview',
  pagination: selectedCashRecebimentos.value.length > 5,
  pageLength: 5,
  columns: [
    { data: 'recebimento_data', title: t('Data'), renderType: 'date', dataType: 'date' },
    { data: 'operador_name', title: t('Operador') },
    { data: 'amount_reais', title: t('Valor'), renderType: 'currency', dataType: 'number' }
  ]
}));

const selectedAdvancesPreviewConfig = computed<DataTableConfig>(() => ({
  tableId: 'payroll-advances-selected-existing-preview',
  pagination: selectedAdvancesList.value.length > 5,
  pageLength: 5,
  columns: [
    { data: 'employee_name', title: t('Colaborador') },
    { data: 'title', title: t('Título') },
    { data: 'amount_discounted', title: t('Valor a Descontar'), renderType: 'currency', dataType: 'number' }
  ]
}));

// =============================================================================
// API CALLS
// =============================================================================

// Load current advances
const loadCurrentAdvances = async (): Promise<void> => {
  isLoadingAdvances.value = true;
  try {
    log('EmployeeAdvancesConciliationWizard', 'loadCurrentAdvances', '📥 START - Loading advances from API', {
      periodId: props.periodId,
      currentSelectedDiscounts: JSON.stringify(selectedDiscounts.value)
    });

    const data = await getOpenAdvancesForPeriod(props.periodId) as unknown as AdvancesApiResponse;
    advancesData.value = data;

    log('EmployeeAdvancesConciliationWizard', 'loadCurrentAdvances', '📦 API Response received', {
      advancesCount: data.advances?.length || 0,
      totalOpen: data.total_open,
      availableCashCount: data.available_cash_recebimentos?.length || 0,
      advanceIds: data.advances?.map((a) => ({ id: a.id, title: a.title, remaining: a.amount_remaining })) || []
    });

    // NOTE: Machine context integration is handled by parent via props.initialSelections
    // The selectedDiscounts was already initialized in resetWizard() from props.initialSelections
    log('EmployeeAdvancesConciliationWizard', 'loadCurrentAdvances', '📊 Current selectedDiscounts state', {
      selectedDiscountsAfterLoad: JSON.stringify(selectedDiscounts.value),
      selectedDiscountsKeys: Object.keys(selectedDiscounts.value),
      matchingAdvances: data.advances?.filter((a) => selectedDiscounts.value[String(a.id)] !== undefined).map((a) => a.id) || []
    });

    log('EmployeeAdvancesConciliationWizard', 'loadCurrentAdvances', '✅ END - Load complete', {
      count: data.advances?.length || 0,
      existingAdvancesSelected: existingAdvancesSelected.value,
      existingAdvancesTotal: existingAdvancesTotal.value
    });
  } catch (error) {
    console.error('Error loading advances:', error);
    log('EmployeeAdvancesConciliationWizard', 'loadCurrentAdvances', '❌ Error', error);
    const err = error as Error;
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Erro ao carregar adiantamentos'),
      duration: 5000
    });
  } finally {
    isLoadingAdvances.value = false;
  }
};

// Load cash recebimentos
const loadCashRecebimentos = async (): Promise<void> => {
  isLoadingCashRecebimentos.value = true;
  try {
    log('EmployeeAdvancesConciliationWizard', 'loadCashRecebimentos', 'Loading recebimentos', { periodId: props.periodId });

    const data = await fetchAvailableCashRecebimentos(props.periodId);
    cashRecebimentos.value = (data ?? []) as unknown as CashRecebimentoRow[];

    log('EmployeeAdvancesConciliationWizard', 'loadCashRecebimentos', 'Loaded', { count: cashRecebimentos.value.length });
  } catch (error) {
    console.error('Error loading cash recebimentos:', error);
    const err = error as Error;
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Erro ao carregar recebimentos disponíveis'),
      duration: 5000
    });
  } finally {
    isLoadingCashRecebimentos.value = false;
  }
};

// Handle discount update
const handleDiscountUpdate = ({ itemId, amount }: DiscountUpdatePayload): void => {
  log('EmployeeAdvancesConciliationWizard', 'handleDiscountUpdate', 'Updating discount', { itemId, amount });

  if (amount === null) {
    delete selectedDiscounts.value[String(itemId)];
  } else {
    selectedDiscounts.value[String(itemId)] = amount;
  }
};

// Handle step change
const handleStepChange = (stepIndex: number): void => {
  currentStepIndex.value = stepIndex;

  // Load data when entering specific steps
  if (stepIndex === 0 && currentAdvances.value.length === 0) {
    loadCurrentAdvances();
  }
  if (stepIndex === 1 && cashRecebimentos.value.length === 0) {
    loadCashRecebimentos();
  }
};

// Handle wizard close
const handleClose = (): void => {
  const selectionsToEmit: Record<string, DiscountAmount> = { ...selectedDiscounts.value };
  const selectionsTotal = Object.values(selectionsToEmit).reduce<number>(
    (sum, amount) => sum + (parseFloat(String(amount ?? 0)) || 0),
    0
  );

  log('EmployeeAdvancesConciliationWizard', 'handleClose', '🔒 CLOSING WIZARD - Emitting selections back to parent', {
    selectedDiscountsRaw: JSON.stringify(selectedDiscounts.value),
    selectionsToEmit: JSON.stringify(selectionsToEmit),
    selectionsKeys: Object.keys(selectionsToEmit),
    selectionsValues: Object.values(selectionsToEmit),
    selectionsCount: Object.keys(selectionsToEmit).length,
    selectionsTotal: selectionsTotal
  });

  // Emit current selections back to parent BEFORE closing
  // This allows parent to sync selections to machine context
  emit('update:selections', selectionsToEmit);

  log('EmployeeAdvancesConciliationWizard', 'handleClose', '✅ Emitted update:selections event', {
    emittedPayload: JSON.stringify(selectionsToEmit)
  });

  isVisible.value = false;
  emit('update:visible', false);
  emit('closed');
  resetWizard();
};

// Handle wizard finish
const handleFinish = async (): Promise<void> => {
  isProcessing.value = true;

  try {
    log('EmployeeAdvancesConciliationWizard', 'handleFinish', 'Processing', {
      cashCount: selectedCashRecebimentos.value.length,
      cashTotal: cashSelectedTotal.value,
      advancesCount: existingAdvancesSelected.value,
      advancesTotal: existingAdvancesTotal.value
    });

    // Step 1: Create advances from cash recebimentos
    if (selectedCashRecebimentos.value.length > 0) {
      const recebimentoIds = selectedCashRecebimentos.value.map((rec) => rec.recebimento_id);
      const cashResult = await createCashAdvancesBatch(
        props.periodId,
        recebimentoIds,
        props.franchiseId
      ) as CashAdvancesBatchResult | null;

      if (cashResult && (cashResult.created_count ?? 0) > 0) {
        eventBus.emit('globalMessage.success', {
          message: t('Created {count} advances from cash payments', { count: cashResult.created_count ?? 0 }),
          duration: 3000
        });
      }
    }

    // Step 2: Process advance discounts
    const discounts = Object.entries(selectedDiscounts.value)
      .filter(([, amount]) => Number(amount) > 0)
      .map(([advanceId, amount]) => ({
        advance_id: String(advanceId),  // Keep as string to match API
        amount: parseFloat(String(amount))
      }));

    if (discounts.length > 0) {
      await processDiscounts(props.periodId, discounts);

      eventBus.emit('globalMessage.success', {
        message: t('Adiantamentos processados com sucesso'),
        duration: 3000
      });
    }

    // Emit completed with full selection data for parent to sync to machine context
    emit('completed', {
      cashCreated: selectedCashRecebimentos.value.length,
      advancesProcessed: discounts.length,
      selections: { ...selectedDiscounts.value },
      discounts: discounts
    });

    eventBus.emit('payroll.advances.updated', { periodId: props.periodId });
    handleClose();

  } catch (error) {
    console.error('Error processing advances:', error);
    const err = error as Error;
    eventBus.emit('globalMessage.error', {
      message: err.message || t('Erro ao processar adiantamentos'),
      duration: 5000
    });
  } finally {
    isProcessing.value = false;
  }
};

// Reset wizard state
const resetWizard = (): void => {
  log('EmployeeAdvancesConciliationWizard', 'resetWizard', '🔄 START - Initializing wizard state', {
    propsReceived: {
      visible: props.visible,
      periodId: props.periodId,
      franchiseId: props.franchiseId,
      initialSelectionsRaw: JSON.stringify(props.initialSelections),
      initialSelectionsType: typeof props.initialSelections,
      initialSelectionsKeys: Object.keys(props.initialSelections || {}),
      initialSelectionsValues: Object.values(props.initialSelections || {})
    }
  });

  // Initialize selections from machine context (passed from parent)
  selectedDiscounts.value = { ...props.initialSelections };
  selectedCashRecebimentos.value = [];
  selectedOperadorFilter.value = null;
  currentStepIndex.value = 0;

  const totalFromSelections = Object.values(props.initialSelections || {}).reduce<number>(
    (sum, amount) => sum + (parseFloat(String(amount ?? 0)) || 0),
    0
  );

  log('EmployeeAdvancesConciliationWizard', 'resetWizard', '✅ Wizard state initialized', {
    selectedDiscountsNow: JSON.stringify(selectedDiscounts.value),
    selectedDiscountsKeys: Object.keys(selectedDiscounts.value),
    selectedDiscountsCount: Object.keys(selectedDiscounts.value).length,
    totalFromSelections: totalFromSelections,
    propsInitialSelectionsCount: Object.keys(props.initialSelections || {}).length
  });

  // Debug computed values
  log('EmployeeAdvancesConciliationWizard', 'resetWizard', '📊 Computed values after init', {
    existingAdvancesSelected: existingAdvancesSelected.value,
    existingAdvancesTotal: existingAdvancesTotal.value
  });
};

// Watch for visibility changes
watch(() => props.visible, async (newValue) => {
  log('EmployeeAdvancesConciliationWizard', 'watch:visible', `🔔 Visibility changed to: ${newValue}`, {
    newValue: newValue,
    propsInitialSelections: JSON.stringify(props.initialSelections),
    propsInitialSelectionsKeys: Object.keys(props.initialSelections || {}),
    periodId: props.periodId,
    franchiseId: props.franchiseId
  });

  isVisible.value = newValue;
  if (newValue) {
    log('EmployeeAdvancesConciliationWizard', 'watch:visible', '🚀 WIZARD OPENING - About to call resetWizard()');
    resetWizard();

    log('EmployeeAdvancesConciliationWizard', 'watch:visible', '📥 Loading data from API...');
    await Promise.all([
      loadCurrentAdvances(),
      loadCashRecebimentos()
    ]);

    log('EmployeeAdvancesConciliationWizard', 'watch:visible', '✅ WIZARD READY - Final state', {
      selectedDiscounts: JSON.stringify(selectedDiscounts.value),
      selectedDiscountsCount: Object.keys(selectedDiscounts.value).length,
      existingAdvancesSelected: existingAdvancesSelected.value,
      existingAdvancesTotal: existingAdvancesTotal.value,
      currentAdvancesCount: currentAdvances.value.length
    });
  }
});
</script>

<style scoped>
.wizard-step-content {
  padding: 1rem 0;
}

.step-header {
  margin-bottom: 1.5rem;
}

.step-header h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
  font-size: 1.25rem;
}

.step-subtitle {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.kpi-icon.info {
  background: var(--blue-50);
  color: var(--blue-500);
}

.kpi-icon.success {
  background: var(--green-50);
  color: var(--green-500);
}

.kpi-icon.warning {
  background: var(--orange-50);
  color: var(--orange-500);
}

.kpi-content {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.kpi-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

/* Section styling */
.section-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.current-advances-section {
  margin-top: 1.5rem;
}

.employee-section {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  margin-bottom: 1rem;
  overflow: hidden;
}

.employee-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.employee-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.employee-info i {
  color: var(--primary-color);
  font-size: 1.25rem;
}

.employee-name {
  font-weight: 600;
  color: var(--text-color);
}

.employee-total {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.total-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.total-value {
  font-weight: 700;
  font-size: 1.125rem;
  color: var(--primary-color);
}

.advances-list {
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.advance-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.advance-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.advance-title {
  font-weight: 600;
  color: var(--text-color);
}

.advance-date {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

.advance-amounts {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: right;
}

.amount-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.amount-label {
  color: var(--text-color-secondary);
}

.amount-value {
  font-weight: 600;
  color: var(--text-color);
}

.amount-remaining {
  color: var(--orange-600);
}

.amount-row.selected .amount-value {
  color: var(--primary-color);
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 2px dashed var(--surface-200);
}

.empty-state i {
  font-size: 3rem;
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.empty-state h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

/* Info message */
.info-message {
  margin-bottom: 1.5rem;
}

/* Filters */
.filters-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.filter-dropdown {
  min-width: 200px;
}

/* Table styling */
.cash-recebimentos-table {
  margin-bottom: 1rem;
}

.amount-value {
  font-weight: 600;
  color: var(--text-color);
}

.amount-selected {
  color: var(--primary-color);
}

/* Selection summary */
.selection-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 8px;
  margin-top: 1rem;
}

.selection-info {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.selection-info strong {
  color: var(--text-color);
}

.no-selection {
  font-style: italic;
}

/* Employees list */
.employees-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Final summary */
.final-summary {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.summary-header i {
  font-size: 1.25rem;
  color: var(--green-500);
}

.summary-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.summary-body {
  padding: 1.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-100);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.total {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 2px solid var(--surface-200);
  border-bottom: none;
  font-size: 1.1rem;
}

.summary-row .label {
  color: var(--text-color-secondary);
}

.summary-row .value {
  font-weight: 600;
  color: var(--text-color);
}

.summary-row .value.highlight {
  color: var(--orange-600);
}

.summary-row.total .label,
.summary-row.total .value {
  font-weight: 700;
  color: var(--text-color);
}

/* Selected preview */
.selected-preview {
  margin-top: 1rem;
}

.preview-table.compact {
  font-size: 0.9rem;
}

/* Responsive */
@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
  }

  .filter-dropdown {
    min-width: 100%;
  }
}
</style>
